import ReactDOM from "react-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { APIClient } from "api/apiCore";
import * as url from "api/urls"
import { backdropStyle, modalStyle } from "DialogProviderShare";
import { Utils } from "helpers/utils";
import { useBackButtonClose, useRedux } from "hooks";
import Loader from "components/Loader";
import SelectDropdownEmp from "components/SelectDropdownEmp";

interface TaskApproverData {
  taskId?: string;
  taskName?: string;
  approveStatus?: string | null;
  approveRemark?: string | null;
  requestBy?: number | null;
  reqName?: string | null;
  reqHId?: number | null;
  reqHName?: string | null;
  respondId?: number | null;
  respName?: string | null;
  respHId?: number | null;
  respHName?: string | null;
  assignerId?: number | null;
  assignerName?: string | null;
  approvalFlow?: string | null;
}

// Keys match the raw json_agg/row_to_json output from sp_findapproverbytaskid (lowercase, not camelCase)
interface ApprovalFlowStep {
  seq: number;
  userid: number;
  username: string;
  rolename: string;
  roledisplayname: string;
  userfullname: string;
  action: string;
  actiondate: string | null;
  remark: string | null;
}

interface ApproverRow {
  id: number;
  caption: string;
  userId: number | null;
}

const CAPTION_SUBMITTER = "ผู้ยื่นขออนุมัติ";
//const CAPTION_ROW1 = "ผู้อนุมัติ ของาน";
//const CAPTION_ROW2 = "ผู้อนุมัติ ทำงาน";
const CAPTION_ROW1 = "หัวหน้า ผู้ของาน";
const CAPTION_ROW2 = "หัวหน้า ผู้ทำงาน";
const CAPTION_DEFAULT_NEW_ROW = "ผู้ตรวจสอบ";

export function TaskApproverPortal({ taskId, onClose }: { taskId: string, onClose: (v: any) => void }) {
  const { useAppSelector } = useRedux();
  const myDisplayName = useAppSelector((state: any) => state.Profile2.displayName2 ?? state.Profile2.displayName);
  const myUserId = useAppSelector((state: any) => state.Profile2.userId);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<TaskApproverData | null>(null);
  const [rows, setRows] = useState<ApproverRow[]>([]);

  const idRef = useRef<number>(0);
  const nextId = () => ++idRef.current;

  const buildDefaultRows = (reqHId: number | null, respHId: number | null): ApproverRow[] => {
    const result: ApproverRow[] = [];
    if (reqHId && reqHId !== respHId) {
      result.push({ id: nextId(), caption: CAPTION_ROW1, userId: reqHId });
    }
    if (respHId) {
      result.push({ id: nextId(), caption: CAPTION_ROW2, userId: respHId });
    }
    if (result.length === 0) {
      result.push({ id: nextId(), caption: CAPTION_ROW1, userId: null });
      result.push({ id: nextId(), caption: CAPTION_ROW2, userId: null });
    }
    return result;
  }

  const [showAddApprover, setShowAddApprover] = useState<boolean>(false);

  useBackButtonClose(true, () => onClose(null));

  const loadData = useCallback(() => {
    try {
      setIsLoading(true);
      new APIClient().fnPostForm(url.WP_TK_FIND_APPROVER, { taskId })
        .then(rs => {
          setData(rs ?? null);
          setRows(buildDefaultRows(rs?.reqHId ?? null, rs?.respHId ?? null));
          setIsLoading(false);
        })
        .catch(e2 => {
          Utils.HandleException(e2, true);
          setIsLoading(false);
        });
    } catch (e) {
      Utils.HandleException(e, true);
      setIsLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEsc = useCallback((event: any) => {
    if (event.key === "Escape") onClose(null);
  }, [onClose]);

  useEffect(() => {
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [handleEsc]);

  const isNotYetSendApprove = !data?.approveStatus || data.approveStatus.trim() === "" || data.approveStatus === "Cancel" || data.approveStatus === "Rejected";

  const getApprovalFlowSteps = (): ApprovalFlowStep[] => {
    try {
      return JSON.parse(data?.approvalFlow ?? "") ?? [];
    } catch (e) {
      return [];
    }
  }

  const onRemoveRow = (rowId: number) => {
    setRows(prev => prev.length <= 1 ? prev : prev.filter(r => r.id !== rowId));
  }

  const stripCaptionSuffix = (caption: string) => caption.replace(/\s*#\d+$/, "");

  const onAddRow = (rowId: number, insertBefore: boolean) => {
    const idx = rows.findIndex(r => r.id === rowId);
    const insertAt = idx < 0 ? rows.length : (insertBefore ? idx : idx + 1);

    let defaultCaption: string;
    if (insertBefore) {
      defaultCaption = CAPTION_DEFAULT_NEW_ROW;
    } else {
      const clickedRow = idx >= 0 ? rows[idx] : null;
      const baseCaption = clickedRow ? stripCaptionSuffix(clickedRow.caption) : CAPTION_DEFAULT_NEW_ROW;
      const matchCount = rows.filter(r => stripCaptionSuffix(r.caption) === baseCaption).length;
      defaultCaption = `${baseCaption} #${matchCount}`;
    }

    const caption = window.prompt("ระบุชื่อตำแหน่งผู้อนุมัติ", defaultCaption);
    if (caption === null) return; // cancelled
    const newRow: ApproverRow = { id: nextId(), caption: caption.trim() || defaultCaption, userId: null };
    setRows(prev => {
      const copy = [...prev];
      copy.splice(insertAt, 0, newRow);
      return copy;
    });
  }

  const onChangeRowUser = (rowId: number, userId: number | null) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, userId } : r));
  }

  const onEditCaption = (rowId: number, currentCaption: string) => {
    const caption = window.prompt("ระบุชื่อตำแหน่งผู้อนุมัติ", currentCaption);
    if (caption === null) return; // cancelled
    const trimmed = caption.trim();
    if (!trimmed) return;
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, caption: trimmed } : r));
  }

  const onMoveRow = (rowId: number, direction: -1 | 1) => {
    setRows(prev => {
      const idx = prev.findIndex(r => r.id === rowId);
      const swapWith = idx + direction;
      if (idx < 0 || swapWith < 0 || swapWith >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[swapWith]] = [copy[swapWith], copy[idx]];
      return copy;
    });
  }

  const onOk = async () => {
    const selectedRows = rows.filter(r => r.userId);
    if (selectedRows.length === 0) {
      Utils.SweetAlert("กรุณาเลือกผู้อนุมัติอย่างน้อย 1 คน");
      return;
    }
    const userIds = selectedRows.map(r => r.userId);
    if (new Set(userIds).size !== userIds.length) {
      Utils.SweetAlert("ไม่สามารถเลือกผู้อนุมัติคนเดียวกันซ้ำในหลายขั้นตอนได้");
      return;
    }

    try {
      setIsLoading(true);
      const steps = selectedRows.map(r => ({ userId: r.userId, roleName: r.caption }));
      await new APIClient().fnPostForm(url.WP_TK_SEND_APPROVAL, { taskId, steps: JSON.stringify(steps) });
      setIsLoading(false);
      onClose({ approvers: selectedRows.map(r => ({ caption: r.caption, userId: r.userId })) });
    } catch (e) {
      setIsLoading(false);
      Utils.HandleException(e, true);
    }
  }

  const onCancelApproval = async () => {
    const reason = window.prompt("ระบุเหตุผลที่ยกเลิกการขออนุมัติ");
    if (reason === null) return; // cancelled prompt
    const trimmed = reason.trim();
    if (!trimmed) {
      Utils.SweetAlert("กรุณาระบุเหตุผลที่ยกเลิก");
      return;
    }

    try {
      setIsLoading(true);
      await new APIClient().fnPostForm(url.WP_TK_CANCEL_APPROVAL, { taskId, reason: trimmed });
      setIsLoading(false);
      onClose({ cancelled: true });
    } catch (e) {
      setIsLoading(false);
      Utils.HandleException(e, true);
    }
  }

  const onApproveStep = async () => {
    try {
      setIsLoading(true);
      await new APIClient().fnPostForm(url.WP_TK_APPROVE_STEP, { taskId });
      setIsLoading(false);
      onClose({ approved: true });
    } catch (e) {
      setIsLoading(false);
      Utils.HandleException(e, true);
    }
  }

  const onRejectStep = async () => {
    const reason = window.prompt("ระบุเหตุผลที่ปฏิเสธการอนุมัติ");
    if (reason === null) return; // cancelled prompt
    const trimmed = reason.trim();
    if (!trimmed) {
      Utils.SweetAlert("กรุณาระบุเหตุผลที่ปฏิเสธ");
      return;
    }

    try {
      setIsLoading(true);
      await new APIClient().fnPostForm(url.WP_TK_REJECT_STEP, { taskId, reason: trimmed });
      setIsLoading(false);
      onClose({ rejected: true });
    } catch (e) {
      setIsLoading(false);
      Utils.HandleException(e, true);
    }
  }

  const onRecallStep = async () => {
    try {
      setIsLoading(true);
      await new APIClient().fnPostForm(url.WP_TK_RECALL_STEP, { taskId });
      setIsLoading(false);
      onClose({ recalled: true });
    } catch (e) {
      setIsLoading(false);
      Utils.HandleException(e, true);
    }
  }

  return ReactDOM.createPortal(
    <div style={backdropStyle}>
      {isLoading && (<Loader />)}
      <div style={modalStyle}>
        <h3 style={{ marginTop: 0, borderBottom: "dashed 1px var(--bs-secondary)" }}>
          ขออนุมัติ ทำงาน
          {data?.taskName && (<div className="fz-10 text-secondary">{data.taskName}</div>)}
        </h3>

        <Row className="mb-2">
          <Col xs={12} md={4} className="mb-2 mb-md-0">
            <div className="fz-10 text-secondary">ผู้มอบหมายงาน</div>
            <div>{data?.assignerName || "-"}</div>
          </Col>
          <Col xs={12} md={4} className="mb-2 mb-md-0">
            <div className="fz-10 text-secondary">ผู้ของาน</div>
            <div>{data?.reqName || "-"}</div>
          </Col>
          <Col xs={12} md={4}>
            <div className="fz-10 text-secondary">ผู้รับผิดชอบงาน</div>
            <div>{data?.respName || "-"}</div>
          </Col>
        </Row>

        <div style={{ borderBottom: "dashed 1px var(--bs-secondary)", marginBottom: 8 }}></div>

        {isNotYetSendApprove && (
          <div>
            <div className="mb-2">
              <div className="text-secondary">{CAPTION_SUBMITTER}</div>
              <div className="d-flex align-items-center" style={{ gap: 4 }}>
                <div style={{ flex: 1 }}>{myDisplayName || "-"}</div>
              </div>
            </div>
            {rows.map((row, index) => (
              <div key={row.id} className="mb-2">
                <div className="hand hover text-primary" title="คลิกเพื่อแก้ไขชื่อตำแหน่ง" onClick={() => onEditCaption(row.id, row.caption)}>{row.caption}</div>
                <div className="d-flex align-items-center" style={{ gap: 4 }}>
                  <div style={{ flex: 1 }}>
                    <SelectDropdownEmp isDisabled={false} selectedUserId={row.userId} onUserChanged={(e) => onChangeRowUser(row.id, e)} />
                  </div>
                  <button type="button" className="btn btn-outline-secondary btn-sm" title="เลื่อนขึ้น" disabled={index === 0} onClick={() => onMoveRow(row.id, -1)}><i className="fas fa-chevron-up"></i></button>
                  <button type="button" className="btn btn-outline-secondary btn-sm" title="เลื่อนลง" disabled={index === rows.length - 1} onClick={() => onMoveRow(row.id, 1)}><i className="fas fa-chevron-down"></i></button>
                  <button type="button" className="btn btn-outline-secondary btn-sm" title="แทรกก่อนแถวนี้" onClick={() => onAddRow(row.id, true)}><i className="fas fa-chevron-left"></i></button>
                  <button type="button" className="btn btn-outline-secondary btn-sm" title="เพิ่มถัดจากแถวนี้" onClick={() => onAddRow(row.id, false)}><i className="fas fa-plus"></i></button>
                  <button type="button" className="btn btn-outline-danger btn-sm" title="ลบแถวนี้" onClick={() => onRemoveRow(row.id)}><i className="fas fa-trash-alt"></i></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!isNotYetSendApprove && (
          <div className="row">
            <div className="col-12">
              <div className="mb-2 d-flex align-items-center" style={{ gap: 6 }}>
                <span>สถานะอนุมัติ:</span>
                <i className={Utils.GetDSignApproveStatusIconClass(data?.approveStatus)} title={data?.approveStatus ?? ""}></i>
                <span>{Utils.GetDSignApproveStatusText(data?.approveStatus)}</span>
                {data?.approveRemark && (<span className="text-danger">เหตุผล : {data.approveRemark}</span>)}
              </div>
              {getApprovalFlowSteps().map((step) => (
                <div key={step.seq} className="d-flex align-items-center mb-1" style={{ gap: 8 }}>
                  <i className={Utils.GetDSignActionIconClass(step.action)} title={step.action}></i>
                  <span style={{ flex: 1 }}>
                    {step.username}
                    {step.actiondate && (<span className="text-secondary">&nbsp;({Utils.GetDMYHHMM(step.actiondate)})</span>)}
                    {step.remark && (<span className="text-danger">&nbsp;เหตุผล : {step.remark}</span>)}
                  </span>
                  {step.userid === myUserId && (step.action === "Pending" || step.action === "None") && (
                    <>
                      <button className="btn btn-success btn-sm" onClick={onApproveStep}>Approve</button>
                      <button className="btn btn-danger btn-sm" onClick={onRejectStep}>Reject</button>
                    </>
                  )}
                  {step.userid === myUserId && step.action === "Approved" && data?.approveStatus !== "Finished" && (
                    <button className="btn btn-warning btn-sm" onClick={onRecallStep}>Recall</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, borderTop: "dashed 1px var(--m2-line-divider-color)", paddingTop: 8 }}>
          <div style={{ display: "flex", gap: 8 }}>
            {data?.approveStatus === "Open" && (
              <button className="btn btn-outline-danger" onClick={onCancelApproval}>Cancel Approval</button>
            )}
            {data?.approveStatus === "Open" && (
              <button className="btn btn-outline-primary" onClick={() => setShowAddApprover(true)}>เพิ่มผู้อนุมัติ</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-warning" onClick={() => onClose(null)}>ปิด</button>
            {isNotYetSendApprove && (<button className="btn btn-primary" onClick={onOk}>Send Approval</button>)}
          </div>
        </div>
      </div>
      {showAddApprover && (
        <AddApproverPopup
          taskId={taskId}
          steps={getApprovalFlowSteps().filter(s => s.seq > 1)}
          onClose={(added) => {
            setShowAddApprover(false);
            if (added) loadData();
          }}
        />
      )}
    </div>,
    document.body
  );
}

function AddApproverPopup({ taskId, steps, onClose }: { taskId: string, steps: ApprovalFlowStep[], onClose: (added: boolean) => void }) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [roleName, setRoleName] = useState<string>(CAPTION_DEFAULT_NEW_ROW);
  const [referenceUserId, setReferenceUserId] = useState<number | null>(steps[0]?.userid ?? null);
  const [placement, setPlacement] = useState<"Before" | "After">("After");

  useBackButtonClose(true, () => onClose(false));

  const onUserChanged = (newUserId: number | null) => {
    setUserId(newUserId);
    if (!newUserId) return;
    new APIClient().fnPostForm(url.WP_TK_GET_USER_POSITION, { userId: newUserId })
      .then(rs => setRoleName(rs?.positionName?.trim() || CAPTION_DEFAULT_NEW_ROW))
      .catch(e => Utils.HandleException(e, true));
  }

  const onSubmit = async () => {
    if (!userId) {
      Utils.SweetAlert("กรุณาเลือกผู้อนุมัติที่ต้องการเพิ่ม");
      return;
    }
    if (!referenceUserId) {
      Utils.SweetAlert("กรุณาเลือกตำแหน่งอ้างอิง");
      return;
    }
    if (!roleName.trim()) {
      Utils.SweetAlert("กรุณาระบุชื่อตำแหน่งผู้อนุมัติ");
      return;
    }

    try {
      setIsLoading(true);
      await new APIClient().fnPostForm(url.WP_TK_ADD_APPROVER_STEP, { taskId, userId, roleName: roleName.trim(), referenceUserId, placement });
      setIsLoading(false);
      onClose(true);
    } catch (e) {
      setIsLoading(false);
      Utils.HandleException(e, true);
    }
  }

  return ReactDOM.createPortal(
    <div style={backdropStyle}>
      {isLoading && (<Loader />)}
      <div style={modalStyle}>
        <h3 style={{ marginTop: 0, borderBottom: "dashed 1px var(--bs-secondary)" }}>เพิ่มผู้อนุมัติเข้าร่วมขั้นตอน</h3>

        <div className="mb-2">
          <div className="fz-10 text-secondary">ผู้อนุมัติที่ต้องการเพิ่ม</div>
          <SelectDropdownEmp isDisabled={false} selectedUserId={userId} onUserChanged={onUserChanged} />
        </div>

        <div className="mb-2">
          <div className="fz-10 text-secondary">ชื่อตำแหน่ง</div>
          <input type="text" className="form-control" value={roleName} onChange={e => setRoleName(e.target.value)} />
        </div>

        <div className="mb-2">
          <div className="fz-10 text-secondary">ตำแหน่งที่จะเพิ่ม</div>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <select className="form-select" value={placement} onChange={e => setPlacement(e.target.value as "Before" | "After")}>
              <option value="Before">ก่อนหน้า</option>
              <option value="After">ต่อจาก</option>
            </select>
            <select className="form-select" value={referenceUserId ?? ""} onChange={e => setReferenceUserId(e.target.value ? Number(e.target.value) : null)}>
              {steps.map(s => (<option key={s.seq} value={s.userid}>{s.username}</option>))}
            </select>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12, borderTop: "dashed 1px var(--m2-line-divider-color)", paddingTop: 8 }}>
          <button className="btn btn-warning" onClick={() => onClose(false)}>ปิด</button>
          <button className="btn btn-primary" onClick={onSubmit}>เพิ่ม</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
